import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Radio,
  Share2,
  Wrench,
  Users,
  Megaphone,
  Factory,
  FileText,
  BarChart3,
  ShieldCheck,
  Car,
  CloudRain,
  Leaf,
  ArrowRight,
  Info,
  Lock
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import PageTitleBar from '../components/common/PageTitleBar/PageTitleBar';
import './Home.css';
import './styles/pages-common.css';

// 13 Card Illustration Images
import img01 from '../assets/01-government-dashboards.jpg';
import img02 from '../assets/02-core-pole-environmental-tracking.jpg';
import img03 from '../assets/03-asset-inventory-management.jpg';
import img04 from '../assets/04-maintenance.jpg';
import img05 from '../assets/05-supplier-contractor-management.jpg';
import img06 from '../assets/06-digital-advertising-management.jpg';
import img07 from '../assets/07-production-deployment-site-surveys.jpg';
import img08 from '../assets/08-finance-order-invoice-management.jpg';
import img09 from '../assets/09-integrated-analytics-dashboards.jpg';
import img10 from '../assets/10-security-intelligence.jpg';
import img11 from '../assets/11-traffic-intelligence.jpg';
import img12 from '../assets/12-weather-intelligence.jpg';
import img13 from '../assets/13-environmental-ai.jpg';

// Bottom panoramic landscape banner
import bottomBannerImg from '../assets/home-bottom-banner2.png';

/**
 * Role-Based Access Control Checker
 */
export const canRoleAccessView = (view, userRole) => {
  if (!view) return false;
  const normRole = typeof userRole === 'string'
    ? userRole.replace(/[-_ ]/g, '').toLowerCase()
    : (userRole?.name ? String(userRole.name).replace(/[-_ ]/g, '').toLowerCase() : '');

  // Super Admin can access all active modules
  if (normRole === 'superadmin') return true;

  // Digital Advertising / Analytics is strictly Super Admin only
  if (view === 'analytics') {
    return false;
  }

  // Technical Support can access everything except Analytics
  if (normRole === 'technicalsupport') return true;

  // Government role can only access Government Dashboards (/dashboard)
  if (normRole === 'government') {
    return view === 'dashboard';
  }

  // Maintenance and Research can access Dashboard and Maintenance
  if (normRole === 'maintenance' || normRole === 'research') {
    return view === 'dashboard' || view === 'maintenance';
  }

  // Asset Management can access Dashboard, Pole Designs, Assets List, Supplier List
  if (normRole === 'assetmanagement') {
    return view !== 'maintenance' && view !== 'analytics';
  }

  // Fallback for default views
  return view === 'dashboard';
};

/**
 * 13 Enterprise Modules Definition matching the design layout
 */
const MODULES = [
  {
    id: 'gov-dashboards',
    index: '01',
    title: 'Government Dashboards',
    category: 'Intelligence',
    image: img01,
    icon: Building2,
    description: 'Citywide air quality telemetry, real-time pollution reduction metrics, public health indices, and municipal sector reports.',
    view: 'dashboard',
    targetRoute: '/dashboard',
    accentColor: '#00e5ff',
  },
  {
    id: 'core-pole',
    index: '02',
    title: 'CORE Pole and Environmental Tracking',
    category: 'Operations',
    image: img02,
    icon: Radio,
    description: 'Smart pole hardware configuration, real-time environmental sensor feeds, site telemetry sync, and air-filtration tracking.',
    view: 'pole-designs',
    targetRoute: '/pole-designs',
    accentColor: '#00f0b5',
  },
  {
    id: 'asset-inventory',
    index: '03',
    title: 'Asset and Inventory Management',
    category: 'Operations',
    image: img03,
    icon: Share2,
    description: 'Comprehensive hardware lifecycle management, site deployments, warehouse allocation, component catalogs, and audit logs.',
    view: 'assets-list',
    targetRoute: '/assets-list',
    accentColor: '#00a8ff',
  },
  {
    id: 'maintenance',
    index: '04',
    title: 'Maintenance',
    category: 'Operations',
    image: img04,
    icon: Wrench,
    description: 'Work order dispatch, preventative inspection schedules, diagnostic audit trails, sensor calibration, and field repair logs.',
    view: 'maintenance',
    targetRoute: '/maintenance',
    accentColor: '#ff9f1a',
  },
  {
    id: 'supplier-contractor',
    index: '05',
    title: 'Supplier and Contractor Management',
    category: 'Enterprise',
    image: img05,
    icon: Users,
    description: 'Authorized vendor network, contractor compliance tracking, component procurement, SLAs, and performance ratings.',
    view: 'supplier-list',
    targetRoute: '/supplier-list',
    accentColor: '#b366ff',
  },
  {
    id: 'digital-advertising',
    index: '06',
    title: 'Digital Advertising Management',
    category: 'Enterprise',
    image: img06,
    icon: Megaphone,
    description: 'Integrated digital display analytics, ad campaign scheduling, impression telemetry, dynamic content, and monetization insights.',
    view: null,
    targetRoute: null,
    accentColor: '#ff2a85',
  },
  {
    id: 'production-deployment',
    index: '07',
    title: 'Production and Deployment Management',
    category: 'Operations',
    image: img07,
    icon: Factory,
    description: 'Assembly pipeline coordination, geospatial site feasibility surveys, ground team staging, and commissioning workflows.',
    view: null,
    targetRoute: null,
    accentColor: '#9933ff',
  },
  {
    id: 'finance-order-invoice',
    index: '08',
    title: 'Finance, Order and Invoice Management',
    category: 'Enterprise',
    image: img08,
    icon: FileText,
    description: 'Capital expenditure tracking, municipal purchase orders, contractor billing reconciliations, and contract lifecycle management.',
    view: null,
    targetRoute: null,
    accentColor: '#00e6a8',
  },
  {
    id: 'integrated-analytics',
    index: '09',
    title: 'Integrated Analytics/Dashboards',
    category: 'Intelligence',
    image: img09,
    icon: BarChart3,
    description: 'Cross-functional intelligence aggregations, unified KPI summaries, macro environmental reporting, and AI predictive trends.',
    view: 'analytics',
    targetRoute: '/analytics',
    accentColor: '#0080ff',
  },
  {
    id: 'security-intelligence',
    index: '10',
    title: 'Security Intelligence',
    category: 'Intelligence',
    image: img10,
    icon: ShieldCheck,
    description: 'Perimeter surveillance feeds, physical tampering detection, cybersecurity posture, and emergency response triggers.',
    view: null,
    targetRoute: null,
    accentColor: '#ff334b',
  },
  {
    id: 'traffic-intelligence',
    index: '11',
    title: 'Traffic Intelligence',
    category: 'Intelligence',
    image: img11,
    icon: Car,
    description: 'Real-time vehicular flow tracking, congestion mitigation algorithms, smart signal coordination, and pedestrian heatmaps.',
    view: null,
    targetRoute: null,
    accentColor: '#ff8800',
  },
  {
    id: 'weather-intelligence',
    index: '12',
    title: 'Weather Intelligence',
    category: 'Intelligence',
    image: img12,
    icon: CloudRain,
    description: 'Micro-climate weather forecasting, hyper-local barometric telemetry, storm alerts, and atmospheric dispersion models.',
    view: null,
    targetRoute: null,
    accentColor: '#00ccff',
  },
  {
    id: 'environmental-ai',
    index: '13',
    title: 'Environmental AI',
    category: 'Intelligence',
    image: img13,
    icon: Leaf,
    description: 'Generative climate modeling, automated carbon offset optimization, neural pollution forecasts, and ecological action agents.',
    view: null,
    targetRoute: null,
    accentColor: '#10d876',
  },
];

const Home = ({ setView }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Fetch updated user details if needed
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return null;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch user details');
      const data = await response.json();

      if (data.status && data.data?.user) {
        const userData = data.data.user;
        if (userData.profile_picture && !userData.picture) {
          userData.picture = userData.profile_picture;
        }
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Error fetching user data in Home:', error);
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        return parsed;
      }
      return null;
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const userRole = user?.role || 'Super Admin';
  const userName = user?.firstname
    ? `${user.firstname} ${user.lastname || ''}`.trim()
    : 'Pradeep H';

  const handleCardClick = (moduleItem, hasAccess) => {
    if (!moduleItem.view || !hasAccess) return;
    if (setView) {
      setView(moduleItem.view);
    }
  };

  // Sort modules dynamically based on user role:
  // 1. Active items first
  // 2. Restricted items second
  // 3. Coming Soon items last
  const sortedModules = useMemo(() => {
    const getStatusWeight = (item) => {
      const isImplemented = item.view !== null;
      if (!isImplemented) return 3; // Coming Soon
      const hasRoleAccess = canRoleAccessView(item.view, userRole);
      return hasRoleAccess ? 1 : 2; // 1 = Active, 2 = Restricted
    };

    return [...MODULES].sort((a, b) => {
      const weightA = getStatusWeight(a);
      const weightB = getStatusWeight(b);
      if (weightA !== weightB) {
        return weightA - weightB;
      }
      return parseInt(a.index, 10) - parseInt(b.index, 10);
    });
  }, [userRole]);

  return (
    <div className="page-root home-page-container">
      {/* Top Header */}
      <InnerHeader
        user={user}
        setView={setView}
        onProfilePhotoSuccess={async () => {
          await fetchUserDetails();
        }}
      />

      {/* Page Title Bar */}
      <PageTitleBar title="Home" setView={setView} activeBtn="home" />

      {/* Hero Welcome Banner */}
      <div className="home-hero-banner">
        <div className="home-hero-left">
          <div className="home-hero-pill">
            <span className="hero-pill-dot"></span>
            <span>ATMOSOS PLATFORM • UNIFIED COMMAND CENTER</span>
          </div>

          <h1 className="home-hero-heading">
            Welcome back, <span className="home-hero-cyan">{userName}</span>
          </h1>

          <p className="home-hero-desc">
            Access enterprise modules, live atmospheric tracking, and infrastructure operations tailored for your role.
          </p>
        </div>
      </div>

      {/* 13 Cards Grid (4 Columns) */}
      <div className="home-grid-container">
        <div className="home-cards-grid">
          {sortedModules.map((item, idx) => {
            const IconComponent = item.icon;
            const isImplemented = item.view !== null;
            const hasRoleAccess = isImplemented && canRoleAccessView(item.view, userRole);
            const isClickable = isImplemented && hasRoleAccess;

            let statusType = 'coming-soon';
            let statusLabel = 'Coming Soon';

            if (isImplemented) {
              if (hasRoleAccess) {
                statusType = 'active';
                statusLabel = 'Active';
              } else {
                statusType = 'restricted';
                statusLabel = 'Restricted';
              }
            }

            return (
              <motion.div
                key={item.id}
                className={`home-module-card ${isClickable ? 'card-clickable' : 'card-disabled'} ${statusType}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: idx * 0.025 }}
                onClick={() => handleCardClick(item, hasRoleAccess)}
                style={{
                  '--card-accent': item.accentColor,
                }}
              >
                {/* Card Header: Index & Badges */}
                <div className="home-card-header">
                  <span className="home-card-num">
                    {/* {item.index} */}{item.category}

                  </span>

                  <div className="home-card-badges">
                    {/* <span className="home-badge-cat"></span> */}
                    <span className={`home-badge-status ${statusType}`}>
                      {statusType === 'active' && <span className="status-dot"></span>}
                      {statusType === 'coming-soon' && <span className="status-icon">⏱</span>}
                      {statusType === 'restricted' && <Lock size={10} />}
                      {statusLabel}
                    </span>
                  </div>
                </div>

                {/* Card Artwork Image Container */}
                <div className="home-card-img-wrap">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="home-card-img"
                    loading="lazy"
                  />
                  <div className="home-card-icon-badge">
                    <IconComponent size={18} />
                  </div>
                </div>

                {/* Card Body: Title & Description */}
                <h3 className="home-card-title">{item.title}</h3>
                <p className="home-card-desc">{item.description}</p>

                {/* Card Footer: View Button */}
                <div className="home-card-footer">
                  {isClickable ? (
                    <button
                      type="button"
                      className="home-card-btn btn-active"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (setView) setView(item.view);
                      }}
                      title={`Open ${item.title}`}
                    >
                      <span>View</span>
                      <ArrowRight size={13} className="btn-arrow" />
                    </button>
                  ) : !isImplemented ? (
                    <button
                      type="button"
                      className="home-card-btn btn-disabled"
                      disabled
                      title="Module coming soon"
                    >
                      <span>View</span>
                      <Info size={13} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="home-card-btn btn-disabled btn-locked"
                      disabled
                      title={`Access restricted for ${userRole} role`}
                    >
                      <span>View</span>
                      <Lock size={13} />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Row 4: Columns 2, 3, 4 Panoramic Landscape Banner */}
          <div className="home-bottom-banner-card">
            <img
              src={bottomBannerImg}
              alt="AtmosOS Smart City Eco Network"
              className="home-bottom-banner-img"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <InnerFooter />
    </div>
  );
};

export default Home;
